"use client";;
import firebaseApp from "@/firebase/config/firebaseApp";
import { getMessaging, getToken, isSupported, onMessage, Unsubscribe } from 'firebase/messaging';
import Link from "next/link";
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { isIOS } from "react-device-detect";
import { toast } from "sonner";
import ButtonNF from "../ui/forms/ButtonNF";

/** request & retrieve notification token */
export default function useNotificationToken(autoRequestPermission: boolean = false) {
    const NotificationToken = useContext(NotificationTokenContext);

    useEffect(() => {
        //handle auto-request
        if (autoRequestPermission && "Notification" in window) {
            Notification.requestPermission()
        }
    }, [autoRequestPermission])


    return NotificationToken;
}


interface NotificationTokenContextValues {
    fcmToken: string,
    notificationPermissionStatus: NotificationPermission | undefined,
    /** on ios Notification API is blocked until the user adds the app to the home screen */
    isOnIOS_without_NotificationAPI: boolean,
    ManualSetPermission: (permission: NotificationPermission) => any
}
export const NotificationTokenContext = createContext({
    fcmToken: "",
    notificationPermissionStatus: undefined,
    isOnIOS_without_NotificationAPI: false,
    /** Manual set permission as on ios state might not auto update*/
    ManualSetPermission: () => { }
} as NotificationTokenContextValues)

/** warp all app with context as only 1 is needed */
export function NotificationTokenProvider({ children }: { children: ReactNode }) {
    const [fcmToken, setToken] = useState('');
    const [notificationPermissionStatus, setNotificationPermissionStatus] = useState<NotificationPermission>();

    /** check if current instance is on standalone mode (home screen app) */
    const isOnIOS_without_NotificationAPI = (isIOS && !("Notification" in window))


    /** handle messages when app is focused */
    useEffect(() => {
        let unsubscribe: Unsubscribe | null;
        isSupported().then(supported => {
            if (!supported)
                return;

            const messaging = getMessaging();
            unsubscribe = onMessage(messaging, (payload) => {
                const { notification } = payload;
                if (!notification)
                    return;
                const link = payload?.data?.link || payload.fcmOptions?.link;
                const id = toast.info(<>
                    <div className="flex-1">
                        <h5>{ notification.title }</h5>
                        <p>{ notification.body }</p>
                    </div>
                    { link && <Link href={ link } passHref>
                        <ButtonNF color={ "green" } onClick={ () => toast.dismiss(id) }>
                            פתח
                        </ButtonNF>
                    </Link> }
                </>, {
                    "duration": 60 * 1000,
                });
            });
        })
        return () => {
            unsubscribe && unsubscribe();
        }
    }, [])


    //get current permission status & listen for any changes
    useEffect(() => {
        let notificationPermRef: PermissionStatus;
        //on change event handler
        function onPermissionChange() {
            if (!notificationPermRef)
                return;
            const notificationPermission_parsed = (notificationPermRef.state == "prompt") ? "default" : notificationPermRef.state;
            if (notificationPermission_parsed != notificationPermissionStatus)
                setNotificationPermissionStatus(notificationPermission_parsed)
        }

        if ('serviceWorker' in navigator && "Notification" in window) {
            // get notification permission (without requesting it)
            const permission = Notification.permission
            if (permission != notificationPermissionStatus)
                setNotificationPermissionStatus(permission)

            //get notificationPermission -> add change event listener
            navigator.permissions.query({ name: 'notifications' }).then(function (notificationPerm) {
                //save ref - for cleanup
                notificationPermRef = notificationPerm;
                //listen to event
                notificationPerm.addEventListener("change", onPermissionChange)
            });
        }

        return () => {
            //remove listener
            if (notificationPermRef)
                notificationPermRef?.removeEventListener("change", onPermissionChange)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    //when-ever getting permission - get token & save to state
    useEffect(() => {
        async function fetchToken() {
            if (notificationPermissionStatus === 'granted') {
                try {
                    //get & set token state
                    const messaging = getMessaging(firebaseApp);
                    const currentToken = await getToken(messaging, {
                        vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
                    });
                    if (currentToken) {
                        setToken(currentToken);
                    }
                } catch (error) { console.log('Error retrieving token:', error); }
            }
            else
                setToken("") //reset token if invalid permission
        }
        fetchToken();

        //reset token on permission change change
        return () => {
            setToken("");

        }
    }, [notificationPermissionStatus])


    return <NotificationTokenContext.Provider value={
        {
            fcmToken,
            notificationPermissionStatus,
            ManualSetPermission: (permission: NotificationPermission) => setNotificationPermissionStatus(permission),
            isOnIOS_without_NotificationAPI
        }
    }>
        { children }
    </NotificationTokenContext.Provider>
}